import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { StudentsContext } from "../../../../Context";
import mut from "../../hooks/mut";
import CustomCheckbox from "../../components/CustomCheckbox";
import Staff from "../../components/Staff";
import Text from "../../components/Text";
import PhoneNumber from "../../components/PhoneNumber";
import CustomIconButton from "../../components/CustomIconButton";
import sdate from "@timclark/sdate";
import { usePrimaryColor } from "../../components/PrimaryColorContext";

/**
 * StudentData Component
 * Displays student data with sidebar for selection and detailed card view.
 * Filter checkbox moved to top of sidebar.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.htClass - The class object
 * @param {Function} props.setHtClass - Function to update the class object
 *
 * @author Forge
 * @date 2026-04-10
 * @version 6.0.0
 */
export default function StudentData({ htClass, setHtClass }) {
  const { students, pendingStudents, attendance } = htClass;
  const { findStudent } = useContext(StudentsContext);
  const { primaryColor } = usePrimaryColor();
  const [showAll, setShowAll] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Filter and sort students
  let sts = students;
  sts = sts
    .filter((s) =>
      showAll
        ? true
        : findStudent(s) && findStudent(s).status
        ? ["New", "Active"].includes(findStudent(s).status)
        : false
    )
    .sort((a, b) =>
      findStudent(a).name > findStudent(b).name
        ? 1
        : findStudent(b).name > findStudent(a).name
        ? -1
        : 0
    );

  // Set first student as selected if none selected
  useEffect(() => {
    if (!selectedStudent && sts.length > 0) {
      setSelectedStudent(sts[0]);
    }
  }, [sts, selectedStudent]);

  return (
    <>
      {/* Student Sidebar */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
        {/* Filter Checkbox */}
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/30">
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
            <CustomCheckbox
              checked={showAll}
              onChange={() => setShowAll(!showAll)}
            />
            <span>Show stopped and lead students</span>
          </label>
        </div>

        {/* Student List */}
        <div className="flex-1 overflow-y-auto p-2 min-h-[400px] max-h-[600px]">
          {sts.map((student) => (
            <StudentSidebarItem
              key={student}
              student={student}
              isSelected={selectedStudent === student}
              onSelect={() => setSelectedStudent(student)}
              htClass={htClass}
              attendance={attendance}
            />
          ))}
        </div>
      </div>

      {/* Student Detail */}
      <div className="flex flex-col gap-5">
        {selectedStudent && (
          <StudentCard
            student={selectedStudent}
            htClass={htClass}
            setHtClass={setHtClass}
            attendance={attendance}
            feedbackCategories={htClass.center?.feedbackCategories || []}
          />
        )}

        {/* Pending Students */}
        {pendingStudents && pendingStudents.length > 0 && (
          <div className="border-t border-slate-200 pt-5">
            <h3 className="mb-4 text-base font-black text-slate-900">
              Current students pending
            </h3>
            <div className="flex flex-col gap-3">
              {pendingStudents.map((student) => (
                <DrawPendingStudent key={student._id} student={student} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * StudentSidebarItem Component
 * Displays a student in the sidebar with selection state and problem indicators.
 *
 * @component
 * @param {string} props.student - The student ID
 * @param {boolean} props.isSelected - Whether this student is currently selected
 * @param {Function} props.onSelect - Function to call when student is clicked
 * @param {Object} props.htClass - The class object
 * @param {Array} props.attendance - Array of attendance records
 *
 * @author Forge
 * @date 2026-04-10
 * @version 6.0.0
 */
function StudentSidebarItem({
  student,
  isSelected,
  onSelect,
  htClass,
  attendance,
}) {
  const { primaryColor } = usePrimaryColor();
  const { findStudent } = useContext(StudentsContext);
  const studentData = findStudent(student);
  if (["Pending", "Lead", "Stopped"].includes(studentData?.status)) return null;
  if (!studentData) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-500";
      case "New":
        return `bg-${primaryColor}-800`;
      case "Stopped":
        return "bg-red-500";
      case "Lead":
        return "bg-amber-500";
      default:
        return "bg-gray-500";
    }
  };

  const formattedDate = studentData.dateOfBirth
    ? new sdate(new Date(studentData.dateOfBirth)).fDate()
    : null;

  // Check for problems
  const hasNoBook = !htClass.studentsHaveBooks.includes(student);

  // Calculate attendance rate
  const studentAttendance = attendance.filter((a) => a.student === student);
  const attendanceSummary = calculateAttendanceSummary(studentAttendance);
  const totalAttendance =
    attendanceSummary.attended +
    attendanceSummary.late +
    attendanceSummary.absent +
    attendanceSummary.noShow;
  const attendanceRate =
    totalAttendance > 0
      ? Math.round(
          ((attendanceSummary.attended + attendanceSummary.late) /
            totalAttendance) *
            100
        )
      : 100;

  const hasLowAttendance = attendanceRate < 90 && totalAttendance > 0;
  const hasProblem = hasNoBook || hasLowAttendance;

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-200 mb-1 border ${
        hasProblem && !isSelected
          ? "bg-red-50 border-red-200 hover:border-red-300"
          : "border-transparent hover:bg-gray-50 hover:border-gray-200"
      } ${isSelected ? `bg-${primaryColor}-50 border-${primaryColor}-50 shadow-sm` : ""}`}
      onClick={onSelect}
    >
      <div className="flex-1 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(
              studentData.status
            )}`}
          />
          <span className="text-sm font-semibold text-gray-900 flex-1">
            {studentData.name}
          </span>
          {hasProblem && !isSelected && (
            <div className="flex items-center gap-1">
              {hasNoBook && (
                <div
                  className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center"
                  title="No book"
                >
                  <span className="text-[8px] font-bold text-white">!</span>
                </div>
              )}
              {hasLowAttendance && (
                <div
                  className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center"
                  title={`Low attendance: ${attendanceRate}%`}
                >
                  <span className="text-[8px] font-bold text-white">!</span>
                </div>
              )}
            </div>
          )}
        </div>
        {studentData.phone && (
          <span className="text-xs text-gray-500 flex justify-between">
            <div>
              <PhoneNumber value={studentData.phone} />
            </div>
            {formattedDate && (
              <div className="text-xs text-gray-500">{formattedDate}</div>
            )}
          </span>
        )}
      </div>
      {isSelected && <div className={`flex-shrink-0 text-${primaryColor}-800`} />}
    </div>
  );
}

/**
 * StudentCard Component
 * Displays a single wide student card with organized layout.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.student - The student ID
 * @param {Object} props.htClass - The class object
 * @param {Function} props.setHtClass - Function to update class object
 * @param {Array} props.attendance - Array of attendance records
 * @param {Array} props.feedbackCategories - Array of feedback category names
 *
 * @author Forge
 * @date 2026-04-10
 * @version 10.0.0
 */
function StudentCard({
  student,
  htClass,
  setHtClass,
  attendance,
  feedbackCategories,
}) {
  const { primaryColor } = usePrimaryColor();
  const { findStudent } = useContext(StudentsContext);
  const navigate = useNavigate();

  // Calculate attendance summary
  const studentAttendance = attendance.filter((a) => a.student === student);
  const attendanceSummary = calculateAttendanceSummary(studentAttendance);

  // Calculate feedback summary
  const feedbackSummary = calculateFeedbackSummary(
    studentAttendance,
    feedbackCategories
  );

  const studentData = findStudent(student);

  return (
    <div className={`bg-white border-slate-200 border rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-${primaryColor}-50 transition-all duration-300 overflow-hidden flex flex-col gap-5`}>
      {/* Card Header with Details Button */}
      <div className="px-6 pt-6 flex items-start justify-between">
        <div className="flex-1">
          <div className="text-xl font-bold text-gray-900">
            {studentData.name}
          </div>
        </div>
        <CustomIconButton
          icon="person"
          onClick={() => navigate(`/student/${student}`)}
          tooltip="View student details"
        />
      </div>

      {/* Top Row: Book Status and Notes */}
      <div className="flex flex-col lg:flex-row gap-5 px-6">
        <BookStatusCompact student={student} htClass={htClass} setHtClass={setHtClass} />
        <BookNotesCompact htClass={htClass} student={student} setHtClass={setHtClass} />
      </div>

      {/* Notes Section - Full Width */}
      <div className="px-6">
        <NotesSection student={student} htClass={htClass} />
      </div>

      {/* Stats Section - Full Width */}
      <div className="px-6 pb-6">
        <StatsSectionCompact
          attendanceSummary={attendanceSummary}
          feedbackSummary={feedbackSummary}
          feedbackCategories={feedbackCategories}
        />
      </div>
    </div>
  );
}

/**
 * BookStatusCompact Component
 *
 * @component
 * @param {string} props.student - The student ID
 * @param {Object} props.htClass - The class object
 * @param {Function} props.setHtClass - Function to update class object
 *
 * @author Forge
 * @date 2026-04-10
 * @version 5.0.0
 */
function BookStatusCompact({ student, htClass, setHtClass }) {
  const { primaryColor } = usePrimaryColor();
  const hasBook = htClass.studentsHaveBooks.includes(student);

  return (
    <div className="flex-shrink-0">
      <div className="relative group">
        <div className={`absolute -inset-0.5 bg-gradient-to-r from-${primaryColor}-800 to-[${primaryColor}] rounded-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300 blur`} />
        <div className="relative p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-2 h-2 rounded-full bg-${primaryColor}-800`} />
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              Student materials
            </span>
          </div>
          <div className="flex items-center gap-3 p-2">
            <div className="relative">
              <CustomCheckbox
                checked={hasBook}
                onChange={async (e) => {
                  if (e.target.checked) {
                    setHtClass({
                      ...htClass,
                      studentsHaveBooks: [...htClass.studentsHaveBooks, student],
                    });
                    let htc = await mut("post", `/students-books/${htClass._id}`, {
                      student,
                    });
                    setHtClass({ ...htClass, ...htc });
                    return;
                  }
                  setHtClass({
                    ...htClass,
                    studentsHaveBooks: htClass.studentsHaveBooks.filter(
                      (s) => s !== student
                    ),
                  });
                  let htc = await mut(
                    "post",
                    `/students-not-books/${htClass._id}`,
                    { student }
                  );
                  setHtClass({ ...htClass, ...htc });
                }}
              />
            </div>
            <div className="flex flex-col">
              <span
                className={`text-sm font-bold ${
                  hasBook ? "text-green-600" : "text-gray-400"
                }`}
              >
                {hasBook ? "Has book" : "No book"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * BookNotesCompact Component
 * Compact book notes editor.
 *
 * @component
 * @param {Object} props.htClass - The class object
 * @param {string} props.student - The student ID
 * @param {Function} props.setHtClass - Function to update class object
 *
 * @author Forge
 * @date 2026-04-10
 * @version 5.0.0
 */
function BookNotesCompact({ htClass, student, setHtClass }) {
  const { primaryColor } = usePrimaryColor();
  let value =
    htClass.bookNotes && htClass.bookNotes.find((b) => b.student === student)
      ? htClass.bookNotes.find((b) => b.student === student).note
      : "";

  const update = async (note) => {
    let htc = { ...htClass };
    if (!htc.bookNotes) {
      htc.bookNotes = [{ student, note }];
      setHtClass(htc);
    } else {
      let bookNote = htc.bookNotes.find((b) => b.student === student);
      if (bookNote) {
        bookNote.note = note;
        setHtClass(htc);
      } else {
        htc.bookNotes = [...htc.bookNotes, { student, note }];
        setHtClass(htc);
      }
    }
  };

  return (
    <div className="">
      <div className="relative group">
        <div className={`absolute -inset-0.5 bg-gradient-to-r from-${primaryColor}-800 to-[${primaryColor}] rounded-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300 blur`} />
        <div className="relative p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-2 h-2 rounded-full bg-${primaryColor}-800`} />
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              Course materials
            </span>
          </div>
          <Text
            size="small"
            value={value}
            setValue={update}
            url={`/add-book-note/${htClass._id}/student/${student}`}
            field="note"
            fontSize={12}
            placeholder="Add book notes..."
          />
        </div>
      </div>
    </div>
  );
}

/**
 * NotesSection Component
 *
 * @component
 * @param {string} props.student - The student ID
 * @param {Object} props.htClass - The class object
 * @author Forge
 * @date 2026-04-10
 * @version 6.0.0
 */
function NotesSection({ student, htClass }) {
  const { primaryColor } = usePrimaryColor();
  const [showAllNotes, setShowAllNotes] = useState(false);

  // Get student notes from htClass.allStudents array
  const studentData =
    htClass.allStudents && htClass.allStudents.find((s) => s._id === student);
  const allNotes = studentData && studentData.notes ? studentData.notes : [];

  // Sort notes by date (newest first)
  const sortedNotes = [...allNotes].sort((a, b) => {
    const dateA = new Date(a.date || a.createdAt);
    const dateB = new Date(b.date || b.createdAt);
    return dateB - dateA;
  });

  // Limit to 3 notes unless showAllNotes is true
  const displayedNotes = showAllNotes ? sortedNotes : sortedNotes.slice(0, 3);

  // Format date for display
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="bg-gray-50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
          Notes
        </h4>
        <span className="text-xs font-semibold text-gray-400 bg-gray-200 px-3 py-1 rounded-full">
          ({sortedNotes.length})
        </span>
      </div>
      <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto">
        {sortedNotes.length > 0 ? (
          <>
            {displayedNotes.map((noteObj) => (
              <div
                key={noteObj._id}
                className={`bg-white rounded-lg p-4 border border-gray-200 hover:border-${primaryColor}-50 hover:shadow-sm transition-all duration-200`}
              >
                <div className="text-sm font-medium text-gray-900 leading-relaxed mb-3">
                  {noteObj.note}
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
                  <span className="text-xs font-semibold text-gray-500">
                    {formatDate(noteObj.date || noteObj.createdAt)}
                  </span>
                  {noteObj.recordedBy && (
                    <span className="text-xs text-gray-400">
                      by <Staff id={noteObj.recordedBy} />
                    </span>
                  )}
                  {noteObj.assignedTo && (
                    <span className="text-xs text-gray-400">
                      to <Staff id={noteObj.assignedTo} />
                    </span>
                  )}
                  {noteObj.result && (
                    <span className={`inline-block px-3 py-1 rounded-md text-xs font-bold text-${primaryColor}-800 bg-${primaryColor}-50 border border-${primaryColor}-800`}>
                      {noteObj.result}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {sortedNotes.length > 3 && (
              <button
                onClick={() => setShowAllNotes(!showAllNotes)}
                className={`w-full text-xs font-semibold text-${primaryColor}-800 bg-${primaryColor}-50 border border-${primaryColor}-50 py-3 rounded-lg cursor-pointer transition-all duration-200 mt-2 hover:bg-${primaryColor}-800 hover:text-white hover:-translate-y-0.5 hover:shadow-md`}
              >
                {showAllNotes
                  ? "Show less"
                  : `Show ${sortedNotes.length - 3} more notes`}
              </button>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-sm text-gray-400 italic">
            No notes available
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * StatsSectionCompact Component
 * Compact horizontal stats display.
 *
 * @component
 * @param {Object} props.attendanceSummary - Attendance summary object
 * @param {Object} props.feedbackSummary - Feedback summary object
 * @param {Array} props.feedbackCategories - Array of feedback category names
 *
 * @author Forge
 * @date 2026-04-10
 * @version 8.0.0
 */
function StatsSectionCompact({
  attendanceSummary,
  feedbackSummary,
  feedbackCategories,
}) {
  const { primaryColor } = usePrimaryColor();
  const totalAttendance =
    attendanceSummary.attended +
    attendanceSummary.late +
    attendanceSummary.absent +
    attendanceSummary.noShow;
  const attendanceRate =
    totalAttendance > 0
      ? Math.round(
          ((attendanceSummary.attended + attendanceSummary.late) /
            totalAttendance) *
            100
        )
      : 0;

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white rounded-xl p-5">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Attendance */}
        <div className="flex-1">
          <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
            Attendance
          </h5>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 p-4 bg-gray-100 rounded-lg border-l-4 border-gray-300">
              <span className="text-xs font-semibold text-gray-500 uppercase">
                Attendance Rate:
              </span>
              <span className={`text-2xl font-extrabold ${getRateColor(attendanceRate, primaryColor)}`}>
                {attendanceRate}%
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <MiniBar label="P" count={attendanceSummary.attended} />
              <MiniBar label="L" count={attendanceSummary.late} />
              <MiniBar label="A" count={attendanceSummary.absent} />
              <MiniBar label="N" count={attendanceSummary.noShow} />
            </div>
          </div>
        </div>

        {/* Feedback Scores */}
        {feedbackCategories.length > 0 && (
          <div className="flex-1">
            <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Performance Scores
            </h5>
            <div className="flex flex-wrap gap-3">
              {feedbackCategories.slice(0, 3).map((category) => (
                <div
                  key={category}
                  className={`px-4 py-3 rounded-lg border ${getScoreColorClass(
                    feedbackSummary[category],
                    primaryColor
                  )}`}
                >
                  <span className="text-[0.7rem] font-semibold uppercase">
                    {category.substring(0, 3)}
                  </span>
                  <span className="text-2xl font-extrabold text-gray-900">
                    {feedbackSummary[category]
                      ? feedbackSummary[category].toFixed(1)
                      : "-"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * MiniBar Component
 * Small attendance bar for compact display.
 *
 * @component
 * @param {string} props.label - Single letter label
 * @param {number} props.count - The count value
 *
 * @author Forge
 * @date 2026-04-10
 * @version 3.0.0
 */
function MiniBar({ label, count }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
      <span className="text-xs font-semibold uppercase text-gray-500">
        {label}
      </span>
      <span className="text-xl font-extrabold text-gray-900">{count}</span>
    </div>
  );
}

/**
 * DrawPendingStudent Component
 * Displays a pending student card.
 *
 * @component
 * @param {Object} props.student - The student object
 *
 * @author Forge
 * @date 2026-04-10
 * @version 3.0.0
 */
function DrawPendingStudent({ student }) {
  const statusHistory = student.statusHistory.getLast();

  const isValidBillDate =
    student.nextBillDate && new Date(student.nextBillDate) > new Date();

  return (
    <div className="flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      <div className="flex-1">
        <div className="font-semibold text-gray-900">{student.name}</div>
        {student.phone && (
          <div className="text-sm text-gray-500 mt-1">
            <PhoneNumber value={student.phone} />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-amber-700 uppercase">
            Return Date:
          </span>
          <span className="text-sm font-semibold text-gray-900">
            {new sdate(new Date(statusHistory.returnDate)).fDate()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-amber-700 uppercase">
            Next Bill:
          </span>
          <span
            className={`text-sm font-semibold px-3 py-1 rounded-lg ${
              isValidBillDate
                ? "text-green-600 bg-green-100 border border-green-300"
                : "text-red-600 bg-red-100 border border-red-300"
            }`}
          >
            {student.nextBillDate
              ? new sdate(new Date(student.nextBillDate)).fDate()
              : "None"}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Calculates attendance summary statistics.
 *
 * @function
 * @param {Array} attendanceRecords - Array of attendance records for a student
 * @returns {Object} Summary object with counts for each attendance type
 *
 * @author Forge
 * @date 2026-04-10
 * @version 2.0.0
 */
function calculateAttendanceSummary(attendanceRecords) {
  const summary = {
    attended: 0,
    late: 0,
    absent: 0,
    noShow: 0,
  };

  attendanceRecords.forEach((record) => {
    switch (record.attendance) {
      case 1:
        summary.attended++;
        break;
      case -1:
        summary.late++;
        break;
      case 2:
        summary.absent++;
        break;
      case 3:
        summary.noShow++;
        break;
      default:
        break;
    }
  });

  return summary;
}

/**
 * Calculates feedback summary statistics.
 *
 * @function
 * @param {Array} attendanceRecords - Array of attendance records for a student
 * @param {Array} feedbackCategories - Array of feedback category names
 * @returns {Object} Summary object with average scores for each category
 *
 * @author Forge
 * @date 2026-04-10
 * @version 2.0.0
 */
function calculateFeedbackSummary(attendanceRecords, feedbackCategories) {
  const summary = {};

  feedbackCategories.forEach((category) => {
    const scores = attendanceRecords
      .filter((record) => record.feedback && record.feedback[category])
      .map((record) => record.feedback[category]);

    if (scores.length > 0) {
      const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      summary[category] = average;
    } else {
      summary[category] = null;
    }
  });

  return summary;
}

/**
 * Gets color class for attendance rate.
 *
 * @function
 * @param {number} rate - The attendance rate percentage
 * @returns {string} Color class name
 *
 * @author Forge
 * @date 2026-04-10
 * @version 3.0.0
 */
function getRateColor(rate, primaryColor) {
  if (rate >= 80) return "text-green-600";
  if (rate >= 60) return `text-${primaryColor}-800`;
  if (rate >= 40) return "text-amber-600";
  return "text-red-600";
}

/**
 * Get color class for score.
 *
 * @function
 * @param {number} score - The score value
 * @returns {string} Color class
 *
 * @author Forge
 * @date 2026-04-10
 * @version 3.0.0
 */
function getScoreColorClass(score, primaryColor) {
  if (!score) return "opacity-50 bg-gray-50 border-gray-200";
  if (score >= 4.5) return "bg-green-100 text-green-700 border-green-300";
  if (score >= 3.5) return `bg-${primaryColor}-50 text-${primaryColor}-900 border-${primaryColor}-800`;
  if (score >= 2.5) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-red-100 text-red-700 border-red-300";
}